/**
 * Pure helpers for the Executions monitor's gradual (keyset, no-COUNT) list.
 *
 * The monitor's default view (flat list, newest-first) reads executions through
 * the generic `execution_list_gradual` GraphQL query — newest-first by the fixed
 * `(created_at DESC, id DESC)` keyset, with a `continue_before` cursor for "load
 * older" — instead of the offset-paged `execution_list` subscription, whose
 * eager `execution_count` scans the whole (large, fast-growing) execution table
 * on every push. A lightweight `execution_plain_list` subscription (no COUNT)
 * over the newest window keeps the top of the list live.
 *
 * Execution rows are MUTABLE (status, ended_at, … change while a run is live),
 * unlike record_log rows, so the live merge here REPLACES rows by id rather than
 * only adding unseen ones. These helpers own that accumulation logic so it is
 * unit-testable without React. See
 * operator-task-fe-executions-monitor-gradual-endless-scroll.
 */

export interface GradualExecutionRow {
    id?: string | null;
    created_at?: string | null;
    [key: string]: unknown;
}

/** Keyset cursor sent back to fetch the next (older) page. */
export interface ExecutionCursorInput {
    created_at: string;
    id?: string;
}

/** One page of `execution_list_gradual`. */
export interface GradualExecutionPage {
    execution_list?: Array<GradualExecutionRow | null> | null;
    more_available?: boolean | null;
    continue_before?: { created_at?: string | null; id?: string | null } | null;
}

/** Rows per gradual fetch (initial screenful and each "load older" step). The
 *  gradual field costs about 2 × limit against the 2000 GraphQL cost cap. */
export const EXECUTION_GRADUAL_PAGE_SIZE = 50;

/** Newest rows the live subscription requests per push. The plain-list
 *  subscription costs about (row fields + 1) × limit (≈ 13 × limit for the
 *  listing body), so 50 stays well under the 2000 cost cap. */
export const EXECUTION_LIVE_WINDOW_LIMIT = 50;

/** Order shared by the gradual query and the live subscription. */
export const EXECUTION_GRADUAL_ORDER = "-created_at,-id";

/** Non-null rows of a gradual page, in server order (newest-first). */
export const executionPageRows = (page?: GradualExecutionPage | null): GradualExecutionRow[] =>
    (page?.execution_list ?? []).filter((row): row is GradualExecutionRow => Boolean(row));

/** Continuation cursor for the next older page, or null when history is
 *  exhausted. */
export const nextExecutionCursor = (
    page?: GradualExecutionPage | null
): ExecutionCursorInput | null => {
    const cursor = page?.continue_before;
    if (!cursor?.created_at) {
        return null;
    }
    return cursor.id
        ? { created_at: cursor.created_at, id: cursor.id }
        : { created_at: cursor.created_at };
};

/** Newest-first comparator: created_at DESC, then id DESC (the keyset order). */
export const compareNewestFirst = (a: GradualExecutionRow, b: GradualExecutionRow): number => {
    const ac = a.created_at ?? "";
    const bc = b.created_at ?? "";
    if (ac !== bc) {
        return ac < bc ? 1 : -1;
    }
    const ai = a.id ?? "";
    const bi = b.id ?? "";
    if (ai === bi) {
        return 0;
    }
    return ai < bi ? 1 : -1;
};

/**
 * Append an older page to the accumulated list. A keyset boundary can re-emit
 * the anchor row, so rows already present (by id) are skipped. Existing rows keep
 * their position; unseen incoming rows go at the bottom.
 */
export const appendOlderExecutions = (
    existing: GradualExecutionRow[],
    incoming: GradualExecutionRow[]
): GradualExecutionRow[] => {
    const seen = new Set(existing.map((row) => row.id));
    const merged = existing.slice();
    for (const row of incoming) {
        if (row.id && seen.has(row.id)) {
            continue;
        }
        if (row.id) {
            seen.add(row.id);
        }
        merged.push(row);
    }
    return merged;
};

/**
 * Merge one live push (the newest `windowLimit` matching rows) into the
 * accumulated list:
 *
 * - a pushed row whose id is already loaded REPLACES it (status updates in place);
 * - a pushed row that is not loaded is added and the list is re-sorted
 *   newest-first, so a new execution lands at the top;
 * - a loaded row that falls INSIDE the pushed window but is missing from the push
 *   no longer matches the filter (status changed, trashed, …) and is removed. The
 *   window spans from the newest row down to the oldest pushed row when the push
 *   is full (`incoming.length >= windowLimit`); a short push is the complete
 *   matching set, so every loaded row missing from it is removed.
 *
 * Rows older than the window are left untouched. When nothing changes the SAME
 * array reference is returned, so the caller can skip a state update.
 */
export const mergeLiveExecutions = (
    existing: GradualExecutionRow[],
    incoming: GradualExecutionRow[],
    windowLimit: number
): GradualExecutionRow[] => {
    const pushed = incoming.filter((row) => Boolean(row?.id));
    const pushedById = new Map(pushed.map((row) => [row.id as string, row]));
    const isFullWindow = pushed.length >= windowLimit;
    const windowFloor = isFullWindow
        ? pushed.reduce((oldest, row) => (compareNewestFirst(row, oldest) > 0 ? row : oldest))
        : null;
    const insideWindow = (row: GradualExecutionRow) =>
        windowFloor === null || compareNewestFirst(row, windowFloor) <= 0;

    let changed = false;
    const kept: GradualExecutionRow[] = [];
    const seen = new Set<string>();
    for (const row of existing) {
        const id = row.id ?? "";
        const replacement = id ? pushedById.get(id) : undefined;
        if (replacement) {
            seen.add(id);
            if (replacement !== row && !shallowEqualRows(replacement, row)) {
                changed = true;
                kept.push(replacement);
            } else {
                kept.push(row);
            }
        } else if (insideWindow(row)) {
            changed = true;
        } else {
            kept.push(row);
        }
    }
    const fresh = pushed.filter((row) => !seen.has(row.id as string));
    if (!changed && fresh.length === 0) {
        return existing;
    }
    const merged = kept.concat(fresh);
    merged.sort(compareNewestFirst);
    return merged;
};

const shallowEqualRows = (a: GradualExecutionRow, b: GradualExecutionRow): boolean => {
    const aKeys = Object.keys(a);
    if (aKeys.length !== Object.keys(b).length) {
        return false;
    }
    return aKeys.every((key) => JSON.stringify(a[key]) === JSON.stringify(b[key]));
};

/** Footer line while older executions are loading. No total is shown: computing
 *  it is the full-table COUNT this view avoids. */
export const executionScanProgressLabel = (rowCount: number): string =>
    `Showing ${rowCount} so far, loading older executions…`;
